/**
 * EAUG-77 §10: the edit link (?mode=edit&token=<edit token>) and the menu link
 * (?mode=menu&token=<booking id>) carry a credential in this iframe's URL,
 * and the order-edit API calls carry it in their query string (?token=,
 * ?menuToken=). Sentry records URLs everywhere (request.url, breadcrumbs,
 * spans), so every event, transaction and breadcrumb goes through scrubDeep
 * before it leaves (main.ts).
 */

export const FILTERED = '[Filtered]';

// `token=` / `menuToken=` (any case) at the start, after ? & # ; or after an
// encoded ? or & (%3F, %26), up to the end of the value.
const SENSITIVE_PARAM = /(^|[?&#;]|%3F|%26)((?:menu_?)?token(?:=|%3D))[^&#\s"'<>%]*/gi;
const SENSITIVE_KEY = /^(?:menu_?)?token$/i;
const SDK_INTERNAL_KEY = 'sdkProcessingMetadata';

export function scrubString(value: string): string {
  if (!/token/i.test(value)) return value;
  return value.replace(SENSITIVE_PARAM, (_match, lead: string, name: string) => `${lead}${name}${FILTERED}`);
}

/** Scrubs every string of an event (in place, cycle-safe) and returns it. */
export function scrubDeep<T>(value: T, seen: WeakSet<object> = new WeakSet()): T {
  if (typeof value === 'string') return scrubString(value) as unknown as T;
  if (value === null || typeof value !== 'object') return value;
  const node = value as unknown as object;
  if (seen.has(node)) return value;
  seen.add(node);
  if (Array.isArray(value)) {
    // A query string sent as [key, value] pairs.
    if (value.length === 2 && typeof value[0] === 'string' && SENSITIVE_KEY.test(value[0]) && typeof value[1] === 'string') {
      value[1] = FILTERED;
      return value;
    }
    for (let i = 0; i < value.length; i++) {
      const next = scrubDeep(value[i], seen);
      if (next !== value[i]) value[i] = next;
    }
    return value;
  }
  const record = value as unknown as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    // SDK-internal and deleted before sending (@sentry/core envelope.js), and it
    // holds live objects (scopes, the client, the Vue app): never walk or rewrite it.
    if (key === SDK_INTERNAL_KEY) continue;
    const current = record[key];
    const next = SENSITIVE_KEY.test(key) && typeof current === 'string' ? FILTERED : scrubDeep(current, seen);
    if (next !== current) record[key] = next;
  }
  return value;
}

/**
 * A breadcrumb, scrubbed into a copy. An event reaches beforeSend already
 * normalized into plain copies (@sentry/core prepareEvent), but a breadcrumb
 * reaches beforeBreadcrumb holding the live objects a console call logged
 * (stores, the router): never read into them or write to them. Strings are
 * scrubbed, other primitives kept, and an object in `data` is sent as
 * '[Object]' / '[Array]'.
 */
export function scrubBreadcrumb<B extends { message?: string; data?: Record<string, unknown> }>(breadcrumb: B): B {
  const out = { ...breadcrumb };
  if (typeof out.message === 'string') out.message = scrubString(out.message);
  if (out.data !== null && typeof out.data === 'object') {
    const data: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(out.data)) {
      data[key] = SENSITIVE_KEY.test(key) && typeof value === 'string'
        ? FILTERED
        : Array.isArray(value) ? value.map(scrubLeaf) : scrubLeaf(value);
    }
    out.data = data;
  }
  return out;
}

function scrubLeaf(value: unknown): unknown {
  if (typeof value === 'string') return scrubString(value);
  if (typeof value === 'function') return '[Function]';
  if (value === null || typeof value !== 'object') return value;
  return Array.isArray(value) ? '[Array]' : '[Object]';
}

/** Whether a page URL carries a credential (no session replay then: its DOM recording keeps the URL). */
export function hasSensitiveQuery(search: string): boolean {
  return /(^|[?&])(?:menu_?)?token=/i.test(search);
}
